import React from 'react'
import { Badge } from '@/components/ui/badge'
import { TrendingUp, TrendingDown, Target, Pause } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAnimatedCounter } from '@/hooks/use-animated-counter'
import type { BotStatistics } from '@/services/BotStatisticsService'

interface BotCardProps {
  id: string
  name: string
  statistics: BotStatistics
  onClick: () => void
  className?: string
}

const BotCardComponent = ({ id, name, statistics, onClick, className }: BotCardProps) => {
  const { current_prediction, accuracy_rate, correct_predictions, total_predictions, skip_predictions } = statistics
  
  const wrongPredictions = total_predictions - skip_predictions - correct_predictions

  // Animated counters for smooth number transitions
  const animatedAccuracy = useAnimatedCounter({ 
    target: Math.round(accuracy_rate * 10), 
    duration: 300,
    enabled: true 
  })
  const animatedCorrect = useAnimatedCounter({ 
    target: correct_predictions, 
    duration: 250,
    enabled: true 
  })
  const animatedWrong = useAnimatedCounter({ 
    target: wrongPredictions, 
    duration: 250,
    enabled: true 
  })
  
  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 60) return 'text-success'
    if (accuracy >= 50) return 'text-warning' 
    return 'text-destructive'
  }
  
  const getAccuracyCardColor = (accuracy: number) => {
    if (accuracy >= 60) return 'border-success/50 bg-success/5'
    if (accuracy >= 50) return 'border-warning/50 bg-warning/5'
    return 'border-destructive/50 bg-destructive/5'
  }

  const getBadgeVariant = (prediction: string) => {
    if (prediction === 'WIN') return 'default'
    if (prediction === 'LOSS') return 'destructive'
    return 'outline' // For SKIP - using outline variant for grey appearance
  }

  const getPredictionIcon = (prediction: string) => {
    if (prediction === 'WIN') return <TrendingUp className="h-2 sm:h-3 w-2 sm:w-3" />
    if (prediction === 'LOSS') return <TrendingDown className="h-2 sm:h-3 w-2 sm:w-3" />
    return <Pause className="h-2 sm:h-3 w-2 sm:w-3" /> // For SKIP
  }

  return (
    <div
      onClick={onClick}
      className={cn(
        'group relative cursor-pointer rounded-xl p-2 sm:p-3 transition-all duration-200',
        'border-2 bg-card hover:shadow-lg hover:scale-105',
        'backdrop-blur-sm backdrop-saturate-150',
        getAccuracyCardColor(accuracy_rate),
        className
      )}
    >
      {/* Bot Name */}
      <div className="mb-1 sm:mb-2">
        <h3 className="font-semibold text-[10px] sm:text-xs truncate group-hover:text-primary transition-colors">
          {name}
        </h3>
      </div>

      {/* Accuracy - Large Display */}
      <div className="mb-1 sm:mb-2 text-center">
        <div className="flex items-center justify-center gap-0.5 sm:gap-1 mb-0.5 sm:mb-1">
          <Target className="h-2 sm:h-2.5 w-2 sm:w-2.5 text-muted-foreground" />
          <span className="text-[9px] sm:text-xs text-muted-foreground">Accuracy</span>
        </div>
        <div className={cn(
          'text-lg sm:text-xl font-bold transition-all duration-200', 
          getAccuracyColor(accuracy_rate),
          animatedAccuracy.isAnimating && 'animate-pulse scale-105'
        )}>
          {(animatedAccuracy.current / 10).toFixed(1)}%
        </div>
      </div>

      {/* Stats Row */}
      <div className="flex items-center justify-between mb-1 sm:mb-2 text-[9px] sm:text-xs">
        <div className="flex items-center gap-0.5 sm:gap-1">
          <span className={cn(
            "text-success font-medium transition-all duration-200",
            animatedCorrect.isAnimating && 'animate-pulse scale-110'
          )}>
            ✓ {animatedCorrect.current}
          </span>
        </div>
        <div className="flex items-center gap-0.5 sm:gap-1">
          <span className={cn(
            "text-destructive font-medium transition-all duration-200",
            animatedWrong.isAnimating && 'animate-pulse scale-110'
          )}>
            ✗ {animatedWrong.current}
          </span>
        </div>
      </div>

      {/* Next Prediction */}
      <div className="flex items-center justify-center">
        <Badge 
          variant={getBadgeVariant(current_prediction || 'SKIP')} 
          className="text-[9px] sm:text-xs flex items-center gap-0.5 sm:gap-1 w-full justify-center px-1 sm:px-2 py-0.5"
        >
          {getPredictionIcon(current_prediction || 'SKIP')}
          → {current_prediction || 'SKIP'}
        </Badge>
      </div>

      {/* Hover indicator */}
      <div className="absolute inset-0 rounded-xl ring-2 ring-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
    </div>
  )
}

export const BotCard = React.memo(BotCardComponent, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.name === nextProps.name &&
    prevProps.className === nextProps.className &&
    prevProps.statistics.current_prediction === nextProps.statistics.current_prediction &&
    prevProps.statistics.accuracy_rate === nextProps.statistics.accuracy_rate &&
    prevProps.statistics.correct_predictions === nextProps.statistics.correct_predictions &&
    prevProps.statistics.total_predictions === nextProps.statistics.total_predictions &&
    prevProps.statistics.skip_predictions === nextProps.statistics.skip_predictions
  );
});